/**
 * Service worker registration and update prompt (Req 28.1).
 *
 * `main.tsx` passes `registerSW` from `virtual:pwa-register` (vite-plugin-pwa, Workbox
 * window); tests and browsers without service workers pass nothing and get a no-op. With
 * `registerType: 'prompt'` a new version waits until the operator presses "Update now"
 * (`update()` posts SKIP_WAITING and reloads), so a reload never interrupts a transaction.
 */
export interface RegisterSWOptions {
  immediate?: boolean;
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegisterError?: (error: unknown) => void;
}

export type RegisterSW = (options: RegisterSWOptions) => (reloadPage?: boolean) => Promise<void>;

export interface PwaState {
  /** A new version is installed and waiting. */
  needRefresh: boolean;
  /** The app shell is cached; the console can start offline. */
  offlineReady: boolean;
}

export interface PwaUpdates {
  get(): PwaState;
  subscribe(listener: () => void): () => void;
  /** Activates the waiting version and reloads. */
  update(): Promise<void>;
  dismiss(): void;
}

export function serviceWorkerSupported(nav: Navigator | undefined = globalThis.navigator): boolean {
  return !!nav && 'serviceWorker' in nav;
}

export function createPwaUpdates(register: RegisterSW | undefined): PwaUpdates {
  let state: PwaState = { needRefresh: false, offlineReady: false };
  const listeners = new Set<() => void>();
  const set = (patch: Partial<PwaState>) => {
    state = { ...state, ...patch };
    for (const l of listeners) l();
  };
  const updateSW = register?.({
    immediate: true,
    onNeedRefresh: () => set({ needRefresh: true }),
    onOfflineReady: () => set({ offlineReady: true }),
    onRegisterError: (error) => console.error('service worker registration failed', error),
  });
  return {
    get: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async update() {
      await updateSW?.(true);
    },
    dismiss: () => set({ needRefresh: false, offlineReady: false }),
  };
}
